import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InteractiveQuiz = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const quizSteps = [
    {
      id: 'home-size',
      title: 'What\'s your home size?',
      subtitle: 'This helps us recommend the right power and capacity',
      options: [
        { value: 'studio', label: 'Studio/1BR', description: 'Under 800 sq ft', icon: 'Home' },
        { value: 'medium', label: '2-3 Bedroom', description: '800-2000 sq ft', icon: 'Building' },
        { value: 'large', label: '4+ Bedroom', description: '2000+ sq ft', icon: 'Building2' }
      ]
    },
    {
      id: 'floor-type',
      title: 'What flooring do you have?',
      subtitle: 'Different floors need different cleaning approaches',
      options: [
        { value: 'carpet', label: 'Mostly Carpet', description: 'Deep pile, area rugs', icon: 'Square' },
        { value: 'hardwood', label: 'Hardwood/Laminate', description: 'Smooth surfaces', icon: 'Grid3x3' },
        { value: 'mixed', label: 'Mixed Flooring', description: 'Combination of both', icon: 'Layout' }
      ]
    },
    {
      id: 'lifestyle',
      title: 'What\'s your lifestyle like?',
      subtitle: 'Help us understand your cleaning needs',
      options: [
        { value: 'pets', label: 'Pet Owner', description: 'Dogs, cats, or other pets', icon: 'Heart' },
        { value: 'allergies', label: 'Allergies/Asthma', description: 'Need HEPA filtration', icon: 'Shield' },
        { value: 'busy', label: 'Busy Schedule', description: 'Quick, efficient cleaning', icon: 'Clock' },
        { value: 'thorough', label: 'Deep Cleaning', description: 'Detailed, thorough approach', icon: 'Sparkles' }
      ]
    },
    {
      id: 'budget',
      title: 'What\'s your budget range?',
      subtitle: 'We\'ll find the best value in your price range',
      options: [
        { value: 'budget', label: 'Under $200', description: 'Great value options', icon: 'DollarSign' },
        { value: 'mid', label: '$200 - $500', description: 'Best overall value', icon: 'CreditCard' },
        { value: 'premium', label: '$500+', description: 'Premium features', icon: 'Crown' }
      ]
    }
  ];

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [quizSteps?.[currentStep]?.id]: value };
    setAnswers(newAnswers);

    if (currentStep < quizSteps?.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(newAnswers);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setAnswers({});
  };

  if (!isOpen) return null;

  const currentQuiz = quizSteps?.[currentStep];
  const progress = ((currentStep + 1) / quizSteps?.length) * 100;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-text-primary">Smart Vacuum Finder</h2>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary"
            />
          </div>
          
          <div className="flex items-center justify-between text-sm text-text-secondary mb-2">
            <span>Step {currentStep + 1} of {quizSteps?.length}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-semibold text-text-primary mb-2">
              {currentQuiz?.title}
            </h3>
            <p className="text-text-secondary">
              {currentQuiz?.subtitle}
            </p>
          </div>

          <div className="space-y-4">
            {currentQuiz?.options?.map((option) => (
              <button
                key={option?.value}
                onClick={() => handleAnswer(option?.value)}
                className="w-full p-6 border-2 border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all duration-200 text-left group"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-muted group-hover:bg-primary/10 p-3 rounded-lg transition-colors">
                    <Icon name={option?.icon} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-text-primary mb-1">
                      {option?.label}
                    </h4>
                    <p className="text-text-secondary">
                      {option?.description}
                    </p>
                  </div>
                  <Icon name="ChevronRight" size={20} className="text-text-secondary group-hover:text-primary transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex space-x-3">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={handleBack}
                >
                  Back
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={handleReset}
                className="text-text-secondary"
              >
                Start Over
              </Button>
            </div>
            
            <div className="text-sm text-text-secondary">
              Select an option to continue
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveQuiz;